// ─── Règles du diagnostic digestif à réponses multiples ─────────────────
//
// SOURCE UNIQUE, volontairement SANS AUCUN IMPORT.
//
// Trois points d'entrée appliquent ces règles et doivent appliquer les
// MÊMES : la saisie praticienne (cockpit), la saisie cliente (autre dépôt,
// copie fidèle), et la réception serveur qui rattrape tout le reste — une
// application ancienne, un brouillon obsolète, une requête directe.
//
// L'absence d'import n'est pas un détail : ce fichier est chargé par une
// fonction serverless. Une dépendance ici tirerait tout le graphe des
// prompts dans le bundle.
//
// Règles validées par Anissa le 2026-08-01 (priorité 0) :
//   - « Aucun diagnostic » efface les autres réponses et reste exclusif ;
//   - « Je ne sais pas » fait de même ;
//   - sélectionner un diagnostic efface « Aucun » et « Je ne sais pas » ;
//   - SII + RGO reste autorisé ;
//   - dès qu'aucune MICI n'est cochée, `miciStatut` est effacé — dans
//     l'état, dans le brouillon sauvegardé et dans la donnée envoyée ;
//   - recocher une MICI plus tard ne doit ressusciter aucun ancien statut.

/** Réponses qui ne peuvent cohabiter avec rien d'autre. */
export const VALEURS_EXCLUSIVES = Object.freeze(['aucun', 'jenesaispas']);

/**
 * REPRÉSENTATION CANONIQUE d'un statut effacé : la CHAÎNE VIDE.
 *
 * Convention posée le 2026-08-01 sur demande d'Anissa. La clé est CONSERVÉE
 * et vidée, elle n'est jamais supprimée. Les deux étaient possibles, une
 * seule doit être employée partout — et c'est déjà celle en usage dans les
 * deux dépôts, elle n'était simplement écrite nulle part.
 *
 * Une clé conservée et vide se distingue mal d'une clé absente à la lecture,
 * mais les deux se résolvent en `non_renseigne` : la lecture est donc sûre
 * dans les deux cas. Le choix se joue à l'écriture, où vider est plus sûr
 * que supprimer — un `delete` sur un formulaire fusionné laisserait
 * ressortir l'ancienne valeur de l'objet sous-jacent.
 */
export const STATUT_EFFACE = '';

/**
 * Diagnostics qui ouvrent la question du statut MICI.
 * Doublon assumé de MICI_VALEURS (resolveClinicalState) : ce fichier ne peut
 * rien importer. Un test de cohérence verrouille l'alignement des deux.
 */
export const MICI_SELECTION = Object.freeze(['crohn', 'rch', 'autremici']);

const lc = (v) => String(v ?? '').trim().toLowerCase();

export const estExclusive = (v) => VALEURS_EXCLUSIVES.includes(lc(v));
export const estMici = (v) => MICI_SELECTION.includes(lc(v));

/** Une MICI figure-t-elle dans la sélection ? Accepte scalaire ou tableau. */
export function contientMici(valeurs) {
  return enListe(valeurs).some(estMici);
}

/**
 * Les MICI déclarées, en minuscules et triées. Deux sélections qui portent
 * les mêmes MICI rendent donc la même liste, quel que soit l'ordre de clic.
 * SII et RGO n'en font pas partie : ce ne sont pas des MICI.
 */
export function micisDeclarees(valeurs) {
  return [...new Set(enListe(valeurs).map(lc).filter(estMici))].sort();
}

/**
 * ─── MESURE PROVISOIRE, 2026-08-02 : un statut ne couvre qu'UNE MICI ─────
 *
 * LE TROU. `miciStatut` est un champ UNIQUE, partagé par toutes les MICI.
 * Une cliente coche Crohn, répond « en rémission », puis coche « autre
 * MICI » : le statut reste, sans qu'on lui ait rien redemandé. Le système
 * considère alors les DEUX en rémission et ne bloque pas, alors que la
 * seconde n'a jamais été évaluée.
 *
 * C'est un fail-open dans une règle écrite exprès pour être fail-closed :
 * « seule une rémission confirmée passe ». Une rémission confirmée pour
 * Crohn n'est pas une confirmation pour une autre maladie.
 *
 * Trouvé par Benoit le 2026-08-02 en jouant le parcours sur le staging.
 *
 * CE QUE CETTE MESURE N'EST PAS : une nouvelle règle clinique. Elle empêche
 * seulement une confirmation d'être réutilisée pour une maladie à laquelle
 * elle n'a jamais répondu. Le prix des deux erreurs n'est pas le même :
 * bloquer à tort coûte du temps à Anissa, ne pas bloquer laisse passer un
 * plan sur une MICI peut-être en poussée.
 *
 * PROVISOIRE jusqu'à l'arbitrage d'Anissa : statut global, ou statut par
 * maladie ? Tant qu'elle n'a pas tranché, on bloque.
 */
export function plusieursMici(valeurs) {
  return micisDeclarees(valeurs).length > 1;
}

/**
 * L'ensemble des MICI a-t-il changé entre deux sélections ?
 *
 * Sert à décider s'il faut redemander le statut. On compare les ENSEMBLES,
 * pas les listes : décocher puis recocher la même MICI ne redemande rien,
 * ajouter ou retirer une maladie si.
 */
export function ensembleMiciChange(avant, apres) {
  const a = micisDeclarees(avant);
  const b = micisDeclarees(apres);
  return a.length !== b.length || a.some((v, i) => v !== b[i]);
}

/** Nom du champ qui dit À QUOI le statut MICI répond. */
export const CHAMP_LIAISON_STATUT = 'miciStatutPour';

/**
 * Empreinte canonique d'un ensemble de MICI. Triée et en minuscules, donc
 * indépendante de l'ordre des clics et de la casse.
 */
export function empreinteMici(valeurs) {
  return micisDeclarees(valeurs).join('|');
}

/**
 * ─── POURQUOI LE STATUT PORTE SA QUESTION AVEC LUI ───────────────────────
 *
 * Première version de la protection serveur : « si la requête contient
 * `miciStatut`, c'est une réponse fraîche ». Benoit a demandé la preuve, et
 * elle n'existe pas. L'application envoie le formulaire ENTIER à chaque
 * soumission, pas seulement les champs modifiés :
 *
 *     body: JSON.stringify({ form: { ...values, email } })
 *
 * Une application ancienne transmet donc, dans la MÊME requête, la nouvelle
 * sélection et l'ancien statut hérité. Le serveur l'aurait pris pour une
 * réponse fraîche. Le garde-fou reposait sur une supposition fausse.
 *
 * D'où la liaison explicite. Quand quelqu'un répond au statut, on écrit
 * AUSSI l'empreinte de l'ensemble de MICI auquel il répond. Le serveur
 * n'accepte le statut que si cette empreinte correspond exactement à la
 * sélection courante. Liaison absente ou différente : statut vide, blocage.
 *
 * La donnée se suffit alors à elle-même : plus besoin de deviner ce que la
 * requête « voulait dire », ni de comparer avec l'enregistré. C'est ce qui
 * permet de supprimer l'heuristique précédente au lieu de l'empiler.
 *
 * Conséquence assumée : une application qui n'écrit pas la liaison ne peut
 * plus lever le blocage. C'est le sens de fail-closed, et l'impact mesuré
 * sur les fiches existantes est nul.
 */
export function poserStatutMici(form, statut) {
  const base = form || {};
  const valeur = lc(statut);
  if (valeur === '') {
    return { ...base, miciStatut: STATUT_EFFACE, [CHAMP_LIAISON_STATUT]: STATUT_EFFACE };
  }
  return {
    ...base,
    miciStatut: statut,
    [CHAMP_LIAISON_STATUT]: empreinteMici(base.digestifDiagnostic),
  };
}

/**
 * ─── UN STATUT PAR MALADIE ──────────────────────────────────────────────
 *
 * ARBITRAGE D'ANISSA, 2026-08-02, option A : « Si plusieurs MICI sont
 * cochées, la cliente doit indiquer le statut de chacune séparément. Un
 * statut global ne permet pas de savoir précisément à quelle maladie la
 * réponse se rapporte. »
 *
 * Chaque MICI a donc son propre champ, et sa propre liaison. La liaison
 * devient triviale — un champ ne répond qu'à une maladie — mais elle reste
 * nécessaire : elle prouve que la réponse a été donnée pendant que CETTE
 * maladie était déclarée, et non héritée d'une sélection précédente.
 *
 * La table est explicite plutôt que dérivée d'une règle de nommage. Une
 * règle du type « miciStatut + capitalise(valeur) » se lit bien mais se
 * casse silencieusement sur `autreMici`, dont la casse d'origine diffère de
 * la clé normalisée. Un test verrouille que chaque entrée de MICI_SELECTION
 * a bien son champ : ajouter une MICI sans son statut fera rougir.
 */
export const CHAMPS_STATUT_MICI = Object.freeze({
  crohn: 'miciStatutCrohn',
  rch: 'miciStatutRch',
  autremici: 'miciStatutAutreMici',
});

/** Le champ qui porte le statut de CETTE maladie, ou `null`. */
export function champStatutPour(mici) {
  return CHAMPS_STATUT_MICI[lc(mici)] ?? null;
}

/** Le champ qui dit à quelle maladie ce statut répond. */
export function champLiaisonPour(mici) {
  const champ = champStatutPour(mici);
  return champ ? `${champ}Pour` : null;
}

/**
 * Les statuts par maladie qu'on a le droit de croire, et eux seuls.
 *
 * Un statut n'est retenu que s'il est renseigné ET que sa liaison désigne
 * exactement la maladie qu'il prétend décrire. Une maladie déclarée sans
 * statut fiable est absente du résultat : c'est ce qui rend le blocage
 * automatique côté résolveur, sans qu'il ait à connaître ces règles.
 *
 * REPRISE DE L'ANCIEN CHAMP UNIQUE, et strictement bornée. Une fiche
 * antérieure au 2026-08-02 porte `miciStatut` sans champ par maladie. On
 * l'accepte pour la SEULE maladie déclarée, et seulement si `statutMiciFiable`
 * l'a validée — c'est-à-dire liaison présente et correspondante. Dès qu'il y
 * a deux maladies, l'ancien champ ne vaut plus rien, ce qui était déjà la
 * règle. Aucune donnée n'est réécrite : on lit ce qui existe, on n'invente
 * pas de statut pour une maladie qui n'en a jamais eu.
 */
export function statutsMiciFiables(form) {
  const f = form || {};
  const micis = micisDeclarees(f.digestifDiagnostic);
  const retenus = {};

  for (const mici of micis) {
    const champ = champStatutPour(mici);
    const valeur = champ ? lc(f[champ]) : '';
    if (valeur !== '' && lc(f[`${champ}Pour`]) === mici) {
      retenus[mici] = valeur;
      continue;
    }
    // Reprise de l'ancien champ unique, une seule maladie déclarée.
    if (micis.length === 1 && statutMiciFiable(f)) {
      retenus[mici] = lc(f.miciStatut);
    }
  }
  return retenus;
}

/**
 * Toutes les MICI déclarées sont-elles en rémission, chacune confirmée ?
 *
 * Faux dès qu'une seule manque à l'appel. C'est la règle « seule une
 * rémission confirmée passe », appliquée maladie par maladie au lieu de
 * l'être en bloc.
 */
export function toutesMicisEnRemission(form) {
  const f = form || {};
  const micis = micisDeclarees(f.digestifDiagnostic);
  if (micis.length === 0) return false;
  const statuts = statutsMiciFiables(f);
  return micis.every((m) => statuts[m] === 'remission');
}

/**
 * Le statut enregistré répond-il bien à la sélection actuelle ?
 *
 * Faux si la liaison manque, si elle désigne un autre ensemble, ou si
 * plusieurs MICI sont déclarées — un statut unique ne peut pas en couvrir
 * deux, même correctement lié.
 *
 * ⚠ Ne concerne QUE l'ancien champ unique `miciStatut`. Depuis le
 * 2026-08-02, la lecture qui fait autorité est `statutsMiciFiables`.
 */
export function statutMiciFiable(form) {
  const f = form || {};
  const selection = f.digestifDiagnostic;
  if (!contientMici(selection)) return false;
  if (plusieursMici(selection)) return false;
  if (lc(f.miciStatut) === '') return false;
  return lc(f[CHAMP_LIAISON_STATUT]) === empreinteMici(selection);
}

/** Normalise une valeur en liste, sans rien perdre ni transformer. */
export function enListe(valeurs) {
  if (Array.isArray(valeurs)) return valeurs.filter((v) => lc(v) !== '');
  return lc(valeurs) === '' ? [] : [valeurs];
}

/**
 * Rend cohérente une sélection quelconque, d'où qu'elle vienne.
 *
 * Règle de résolution conservatrice, confirmée par Anissa le 2026-08-01 :
 *
 *   1. une RÉPONSE DIAGNOSTIQUE SPÉCIFIQUE gagne contre une réponse
 *      exclusive contradictoire. On ne perd jamais une déclaration ;
 *   2. entre « Aucun diagnostic » et « Je ne sais pas », c'est « Je ne sais
 *      pas » qui gagne. Une combinaison contradictoire ne doit pas se
 *      résoudre en « aucun diagnostic », qui est une affirmation ; « je ne
 *      sais pas » déclenche une validation, ce qui est le comportement sûr.
 *
 * Vocabulaire, sur sa demande : on écrit « réponse diagnostique spécifique »
 * et non « diagnostic réel ». Le SaaS NORMALISE une déclaration, il ne
 * certifie aucun diagnostic.
 *
 * La casse d'origine est préservée : `autreMici` ne devient pas `autremici`.
 */
export function normaliserSelectionDigestive(valeurs) {
  const liste = enListe(valeurs);
  const vues = new Set();
  const uniques = [];
  for (const v of liste) {
    const k = lc(v);
    if (vues.has(k)) continue;
    vues.add(k);
    uniques.push(v);
  }

  const specifiques = uniques.filter((v) => !estExclusive(v));
  if (specifiques.length > 0) return specifiques;

  const jeNeSaisPas = uniques.find((v) => lc(v) === 'jenesaispas');
  if (jeNeSaisPas) return [jeNeSaisPas];
  const aucun = uniques.find((v) => lc(v) === 'aucun');
  return aucun ? [aucun] : [];
}

/**
 * Coche ou décoche une valeur, en appliquant les règles d'exclusivité.
 * L'entrée n'est jamais mutée.
 */
export function basculerDigestif(valeurs, valeur) {
  const liste = enListe(valeurs);
  const dejaCochee = liste.some((v) => lc(v) === lc(valeur));

  if (estExclusive(valeur)) {
    // Exclusive : soit elle est seule, soit elle n'est pas là.
    return dejaCochee ? [] : [valeur];
  }
  const sansExclusives = liste.filter((v) => !estExclusive(v));
  return dejaCochee
    ? sansExclusives.filter((v) => lc(v) !== lc(valeur))
    : [...sansExclusives, valeur];
}

/**
 * Applique une sélection digestive à un formulaire, en effaçant le statut
 * MICI devenu sans objet.
 *
 * C'est ici que se joue le scénario qui a motivé cette correction : une
 * cliente déclare une MICI, répond « en rémission », se ravise et la
 * décoche. Sans cet effacement, le statut survit ; si elle recoche une MICI
 * plus tard, une rémission jamais reconfirmée lèverait le blocage.
 *
 * @returns {object} un NOUVEAU formulaire, l'entrée n'est pas mutée
 */
/**
 * Efface le statut de toute maladie qui n'est plus déclarée, et sa liaison.
 *
 * Écrit dans un formulaire déjà copié. Rend la liste des champs touchés,
 * pour la trace : ce sont des NOMS de champs, jamais des valeurs cliniques.
 *
 * POURQUOI EFFACER PLUTÔT QUE LAISSER DORMIR. Un statut de maladie décochée
 * est une réponse à une question qui n'est plus posée. La garder, c'est
 * parier que personne ne la relira jamais — et le 2026-08-02 a montré que
 * quelqu'un finit toujours par relire une valeur dormante.
 */
function effacerStatutsSansObjet(cible, selection) {
  const presentes = new Set(micisDeclarees(selection));
  const touches = [];
  for (const [mici, champ] of Object.entries(CHAMPS_STATUT_MICI)) {
    if (presentes.has(mici)) continue;
    const liaison = `${champ}Pour`;
    if (String(cible[champ] ?? '') !== '') {
      cible[champ] = STATUT_EFFACE;
      touches.push(champ);
    }
    if (String(cible[liaison] ?? '') !== '') {
      cible[liaison] = STATUT_EFFACE;
      touches.push(liaison);
    }
  }
  return touches;
}

export function appliquerSelectionDigestive(form, valeurs) {
  const selection = normaliserSelectionDigestive(valeurs);
  const suivant = { ...(form || {}), digestifDiagnostic: selection };
  // Un statut par maladie disparaît avec sa maladie, et lui seul : décocher
  // Crohn ne doit pas effacer la réponse donnée pour une rectocolite.
  effacerStatutsSansObjet(suivant, selection);
  if (!contientMici(selection)) {
    suivant.miciStatut = STATUT_EFFACE;
    suivant[CHAMP_LIAISON_STATUT] = STATUT_EFFACE;
  } else if (ensembleMiciChange((form || {}).digestifDiagnostic, selection)) {
    // 2026-08-02 : l'ensemble des MICI a change, donc le statut repondait a
    // une autre question. On le redemande plutot que de le reconduire : un
    // statut confirme pour Crohn ne dit rien d'une seconde maladie.
    suivant.miciStatut = STATUT_EFFACE;
    suivant[CHAMP_LIAISON_STATUT] = STATUT_EFFACE;
  }
  return suivant;
}

/**
 * Rattrapage serveur : rend cohérent un formulaire entrant, quelle que soit
 * son origine. Ne rejette rien — une cliente ne doit pas perdre sa saisie
 * parce qu'une application ancienne a envoyé une combinaison incohérente.
 *
 * @param {object} [contexte] Ce que le serveur sait en plus du formulaire
 *   fusionne :  = ce qui est enregistre,  = ce que la
 *   requete porte reellement, avant fusion. Sans lui, seules les regles 1
 *   et 2 s appliquent : moins strict, jamais plus permissif.
 *
 * @param {object} [contexte] Ce que le serveur sait en plus du formulaire
 *   fusionné : `precedent` est ce qui est enregistré, `entrant` ce que la
 *   requête porte réellement AVANT fusion. Sans lui, seules les deux
 *   premières règles d'effacement s'appliquent : moins strict, mais jamais
 *   plus permissif.
 *
 * @returns {{form: object, corrections: string[]}} `corrections` ne contient
 *   que des NOMS de champs, jamais de valeur clinique : il part dans les
 *   journaux.
 */
export function assainirFormulaireDigestif(form) {
  const entree = form || {};
  const corrections = [];

  const brut = entree.digestifDiagnostic;
  const aUneReponse = enListe(brut).length > 0;
  const selection = normaliserSelectionDigestive(brut);

  const sortie = { ...entree };

  if (aUneReponse) {
    const avant = enListe(brut).length;
    if (selection.length !== avant || !Array.isArray(brut)) {
      sortie.digestifDiagnostic = selection;
      corrections.push('digestifDiagnostic');
    } else {
      sortie.digestifDiagnostic = selection;
    }
  }

  // Statut MICI sans MICI déclarée : on l'efface, dans tous les cas, y
  // compris quand aucune réponse digestive n'accompagne la requête.
  const statut = String(entree.miciStatut ?? '').trim();
  const declaration = aUneReponse ? selection : brut;

  // ── 2026-08-02 : un statut ne survit jamais à un doute ─────────────────
  //
  // Trois raisons de l'effacer, et la troisième est celle qui manquait.
  //
  //   1. plus aucune MICI déclarée : le statut n'a plus d'objet ;
  //   2. PLUSIEURS MICI pour un seul statut : il ne peut pas les couvrir
  //      toutes, donc il ne vaut pas confirmation ;
  //   3. l'ENSEMBLE des MICI a changé depuis ce qui est enregistré : le
  //      statut répondait à une autre question.
  //
  // La 3 ferme un fail-open que la 2 seule laissait ouvert, signalé par
  // Benoit le 2026-08-02. Sans elle : une cliente déclare Crohn et répond
  // « en rémission », une seconde MICI arrive — le blocage se déclenche,
  // mais le statut RESTE en base. Une application ancienne retire ensuite
  // la seconde MICI sans toucher au statut, et l'ancienne rémission,
  // jamais reconfirmée, redevient valide. Le blocage tombe.
  //
  // On efface donc dans le champ qui fait autorité, plutôt que de compter
  // sur le résolveur pour l'ignorer. Une valeur dormante finit toujours par
  // être relue par quelqu'un.
  //
  // On ne conserve pas la réponse ailleurs : elle répondait à une question
  // qui n'est plus posée, la garder serait garder une donnée trompeuse.
  // `corrections` porte le NOM du champ touché, ce qui suffit à la trace.
  //
  // LA NUANCE QUI ÉVITE DE JETER UNE VRAIE RÉPONSE. La règle 3 ne vise que
  // les RELIQUATS de fusion. Si la requête porte elle-même `miciStatut`,
  // c'est une réponse donnée pour la sélection qu'elle accompagne, et elle
  // doit être gardée : une cliente qui retire une MICI puis répond de
  // nouveau ne doit pas voir sa réponse effacée par le serveur.
  //
  // D'où `contexte.entrant` : le formulaire tel qu'ENVOYÉ, avant fusion
  // avec l'enregistré. Sans lui, la règle 3 ne s'applique pas — un appelant
  // qui ne le fournit pas garde donc l'ancien comportement, en moins strict
  // mais jamais en plus permissif que les règles 1 et 2.
  // La LIAISON tranche, et elle seule. `statutMiciFiable` refuse un statut
  // sans MICI, un statut pour plusieurs MICI, et un statut dont l'empreinte
  // ne correspond pas à la sélection présente.
  //
  // L'heuristique précédente — « si la requête porte le statut, il est
  // frais » — est SUPPRIMÉE, pas empilée : elle reposait sur l'idée que
  // l'application n'envoie que les champs modifiés, alors qu'elle envoie le
  // formulaire entier. Une donnée qui porte sa propre question n'a besoin
  // d'aucune supposition sur l'intention de l'appelant.
  // 2026-08-02, statut par maladie : un statut dont la maladie n'est plus
  // déclarée n'a plus d'objet, exactement comme l'ancien champ unique. Même
  // règle, appliquée maladie par maladie, et côté serveur aussi — une
  // application ancienne peut très bien envoyer les deux.
  corrections.push(...effacerStatutsSansObjet(sortie, declaration));

  const aEffacer = statut !== '' && !statutMiciFiable({ ...entree, digestifDiagnostic: declaration });

  if (aEffacer) {
    sortie.miciStatut = STATUT_EFFACE;
    corrections.push('miciStatut');
  }
  // La liaison ne survit jamais à son statut : la garder laisserait une
  // empreinte orpheline qu'un futur lecteur pourrait croire valide.
  if (aEffacer || statut === '') {
    if (String(entree[CHAMP_LIAISON_STATUT] ?? '') !== '') {
      sortie[CHAMP_LIAISON_STATUT] = STATUT_EFFACE;
      corrections.push(CHAMP_LIAISON_STATUT);
    }
  }

  return { form: sortie, corrections };
}
